import React, {
  BaseSyntheticEvent,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertProps, Button } from 'antd';
import styled from 'styled-components';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './FormValidationAlert.translations.json';
import { FieldValues, useFormContext } from 'react-hook-form';
import { emitFocusFormInput } from '../events/FocusFormInputEvent';
import { ValidationMessagesMap } from '../../utils/ValidationMessagesMap';
import { ErrorAlertWithDrawer } from '../../components/ErrorAlertWithDrawer';
import { suppressWarnings } from '../../utils/schema';
import { useFormSchema, useFormSubmitHandler } from '../AdditionalFormContext';
import { useFormReset } from '../useResetForm';

const { addTranslations, useTranslation } = i18nNsFactory('form-errors2');
addTranslations(translations);

export interface FormValidationAlertProps extends AlertProps {
  tLabel?: (name: string | string[]) => ReactNode;
  validationMessagesMap: ValidationMessagesMap;
  errorAction?: string;
  viewButton?: boolean;
}

export const FormValidationAlert = <FormData extends FieldValues>(
  props: FormValidationAlertProps,
) => {
  const {
    tLabel,
    validationMessagesMap,
    errorAction,
    viewButton = true,
    ...alertProps
  } = props;
  const { t } = useTranslation();
  const [isErrorDrawerOpen, setIsErrorDrawerOpen] = useState(false);
  const pendingFocusNameRef = useRef<string | null>(null);

  const form = useFormContext<FormData>();
  const formSchema = useFormSchema<FormData>();
  const formSubmitHandler = useFormSubmitHandler<FormData>();
  const resetForm = useFormReset<FormData>();

  const handleSubmit = useCallback(
    (data: FormData, event?: BaseSyntheticEvent) => {
      const result = suppressWarnings(formSchema).safeParse(data);
      if (result.success) {
        return resetForm(data, formSubmitHandler(result.data, true, event));
      }
      return () => Promise.resolve();
    },
    [formSchema, formSubmitHandler, resetForm],
  );

  const ignoreWarningsAndSubmit = useCallback(() => {
    form.clearErrors();
    handleSubmit(form.getValues());
    return;
  }, [form, handleSubmit]);

  const { totalWarnings, totalErrors } = validationMessagesMap;

  const onWarningConfirm = useMemo(
    () => form.handleSubmit(handleSubmit, ignoreWarningsAndSubmit),
    [form, handleSubmit, ignoreWarningsAndSubmit],
  );

  const onDrawerOpenChange = useCallback((open: boolean) => {
    if (open || !pendingFocusNameRef.current) return;

    const name = pendingFocusNameRef.current;
    pendingFocusNameRef.current = null;
    queueMicrotask(() => emitFocusFormInput(name));
  }, []);

  const renderErrorHeading = useMemo(
    () =>
      tLabel &&
      ((name: string) => {
        const baseName = name.replace(/\.[0-9]+/g, '');
        const labelKeys = [`${baseName}.labelFull`, `${baseName}.label`];
        if (baseName === 'root') {
          // in case of root-level errors, do not display a title at all if no translation is provided for it
          labelKeys.push('');
        }
        return tLabel(labelKeys);
      }),
    [tLabel],
  );

  return (
    <ErrorAlertWithDrawer
      validationMessagesMap={validationMessagesMap}
      renderErrorHeading={renderErrorHeading}
      renderErrorMeta={(name, _message, index) =>
        name &&
        name !== 'root' &&
        viewButton && (
          <ErrorButton
            id={`view-form-errors-${index}`}
            size="small"
            onClick={() => {
              pendingFocusNameRef.current = name;
              setIsErrorDrawerOpen(false);
            }}
          >
            {t('button.goto')}
          </ErrorButton>
        )
      }
      isErrorDrawerOpen={isErrorDrawerOpen}
      onClickDrawerClose={() => setIsErrorDrawerOpen(false)}
      onClickOpenDrawer={() => setIsErrorDrawerOpen(true)}
      onDrawerOpenChange={onDrawerOpenChange}
      action={
        Boolean(totalWarnings && !totalErrors) && (
          <Button
            id="suppress-form-warnings"
            type={'primary'}
            onClick={onWarningConfirm}
          >
            {t('button.confirm')}
          </Button>
        )
      }
      errorAction={errorAction}
      {...alertProps}
    />
  );
};

const ErrorButton = styled(Button)`
  align-self: flex-start;
`;
